import { useMemo } from 'react'
import {
  type AccountStatusData,
  findInitialStatus,
  type ProgramTaskCriteria,
  type QuizId,
  type WorkflowActionId,
  type WorkflowStageId,
} from '@terros/common'
import { useWorkflowForCanvassFeature } from '../../workflow'
import { type SelectItem } from '../../types'
import { useQuizFeature } from '../../quiz'
import { type TranslateFunction, translateLabels, useTranslation } from '../../i18n'
import { useConversationFeature } from '../../conversation'
import { type FormTaskType } from './types'

export const PLACEHOLDER_STAGE_ID: WorkflowStageId = 'S.PLACEHOLDER'
export const PLACEHOLDER_ACTION_ID: WorkflowActionId = 'A.PLACEHOLDER'
export const PLACEHOLDER_QUIZ_ID: QuizId = 'Quiz.PLACEHOLDER'

export function useTaskTypeOptions(
  currentType: FormTaskType,
  source: 'program' | 'manager'
): SelectItem<FormTaskType>[] {
  const { t } = useTranslation()
  const isRecordingEnabled = useConversationFeature().isEnabled
  const isWorkflowEnabled = useWorkflowForCanvassFeature().isEnabled
  const isQuizEnabled = useQuizFeature().isEnabled

  return useMemo(
    () => getTaskTypeOptions({ t, currentType, isRecordingEnabled, source, isWorkflowEnabled, isQuizEnabled }),
    [t, currentType, isRecordingEnabled, source, isWorkflowEnabled, isQuizEnabled]
  )
}

export function getTaskTypeOptions({
  t,
  currentType,
  source,
  isRecordingEnabled,
  isWorkflowEnabled,
  isQuizEnabled,
}: {
  t: TranslateFunction
  currentType: FormTaskType
  source: 'program' | 'manager'
  isRecordingEnabled: boolean
  isWorkflowEnabled: boolean
  isQuizEnabled: boolean
}): SelectItem<FormTaskType>[] {
  const options = BASE_TYPES.map(toSelectOption)
  if (isRecordingEnabled) {
    options.push(...NEW_RECORDING_TYPES.map(toSelectOption))
  } else {
    options.push(...LEGACY_RECORDING_TYPES.map(toSelectOption))
  }

  if (isWorkflowEnabled) {
    options.push(...WORKFLOW_TYPES.map(toSelectOption))
  } else {
    options.push(...LEGACY_CANVASS_TYPES.map(toSelectOption))
  }

  if (source === 'manager') options.push(...MANAGER_TYPES.map(toSelectOption))
  if (isQuizEnabled) options.push(...QUIZ_TYPES.map(toSelectOption))

  if (!options.some((option) => option.value === currentType)) {
    options.push({ value: currentType, label: stringifyFormTaskType(currentType), disabled: true })
  }
  return translateLabels(options, t)
}

export function getCriteriaForType(type: FormTaskType, statuses: AccountStatusData[]): ProgramTaskCriteria | undefined {
  const initialStatus = findInitialStatus(statuses)

  switch (type) {
    case 'CreateAccount':
      return initialStatus ? { type: 'activity', activityId: initialStatus.statusId, count: 1 } : undefined
    case 'ExternalLink':
      return { type: 'externalLink', url: '', completeWhen: 'clicked' }
    case 'PitchProspect':
      return { type: 'activity', activityId: 'pitch', count: 1 }
    case 'RecordConversation':
      return { type: 'convo', completeWhen: 'recorded', count: 1 }
    case 'RecordEvaluation':
      return { type: 'evaluation', evaluator: 'manager', count: 1 }
    case 'SalesOutput':
      return initialStatus ? { type: 'activity', activityId: initialStatus.statusId, count: 1 } : undefined
    case 'SalesPlanning':
      return { type: 'activity', activityId: 'contact', count: 1 }
    case 'SendMessage':
      return { type: 'videoChat' }
    case 'Shadowing':
      return { type: 'shadow', target: 'manager', completeWhen: 'scheduled', count: 1 }
    case 'TakeQuiz':
      return { type: 'quiz', quizId: PLACEHOLDER_QUIZ_ID }
    case 'WatchVideo':
      return { type: 'video' }
    case 'WorkflowAction':
      return { type: 'workflow', count: 1, stageOrActionId: PLACEHOLDER_ACTION_ID }
    case 'WorkflowStage':
      return { type: 'workflow', count: 1, stageOrActionId: PLACEHOLDER_STAGE_ID }
  }
}

export function stringifyFormTaskType(type: FormTaskType): string {
  switch (type) {
    case 'CreateAccount':
      return 'Create an Account'
    case 'Custom':
      return 'Custom'
    case 'ExternalLink':
      return 'External Link'
    case 'PitchProspect':
      return 'Pitch Prospects'
    case 'SalesOutput':
      return 'Complete an Account Stage'
    case 'RecordConversation':
      return 'Skill Assessment'
    case 'RecordEvaluation':
      return 'Skill Evaluation'
    case 'SalesPlanning':
      return 'Disposition Prospects'
    case 'Shadowing':
      return 'Field Shadowing'
    case 'TakeQuiz':
      return 'Take Quiz'
    case 'WorkflowStage':
      return 'Workflow Stage'
    case 'WorkflowAction':
      return 'Workflow Action'
    case 'SendMessage':
      return 'Connect with Manager via Video Message'
    case 'WatchVideo':
      return 'Watch Training/Content'
    default:
      return type
  }
}

function toSelectOption(type: FormTaskType): SelectItem<FormTaskType> {
  return {
    value: type,
    label: stringifyFormTaskType(type),
  }
}

const LEGACY_CANVASS_TYPES: FormTaskType[] = ['CreateAccount', 'PitchProspect', 'SalesOutput', 'SalesPlanning']
const WORKFLOW_TYPES: FormTaskType[] = ['WorkflowStage', 'WorkflowAction']
const LEGACY_RECORDING_TYPES: FormTaskType[] = ['RecordEvaluation']
const NEW_RECORDING_TYPES: FormTaskType[] = ['RecordConversation']
const BASE_TYPES: FormTaskType[] = ['ExternalLink', 'Shadowing', 'SendMessage', 'WatchVideo']
const MANAGER_TYPES: FormTaskType[] = ['Custom']
const QUIZ_TYPES: FormTaskType[] = ['TakeQuiz']
